import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { FEATURED_ARTICLES, ARTICLES, findArticle } from "@/data/articles";
import { pickLocale } from "@/i18n";

export default function BlogArticle() {
  useReveal();
  const { t } = useTranslation();
  const { slug } = useParams();
  const article = findArticle(slug);

  if (!article) return <Navigate to="/insights" replace />;

  const title = pickLocale(article, "title") || article.title;
  const excerpt = pickLocale(article, "excerpt") || article.excerpt;
  const body = pickLocale(article, "body") || article.body;
  const category = pickLocale(article, "category") || article.category;
  const read = pickLocale(article, "read") || article.read;
  const author = pickLocale(article, "author") || article.author;

  // Related — combine FEATURED_ARTICLES (excluding current) + ARTICLES; pick up to 3
  const pool = [...FEATURED_ARTICLES, ...ARTICLES].filter((a) => a.slug !== article.slug);
  const related = pool.slice(0, 3);

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      {/* HERO IMAGE */}
      <section className="relative w-full h-[60vh] lg:h-[78vh] overflow-hidden bg-[#14355a]">
        <img
          src={article.img}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14355a]/40 via-[#14355a]/20 to-[#14355a]/70" />
        <div className="relative z-10 h-full mx-auto max-w-[1100px] px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-24 text-white">
          <Link
            to="/insights"
            data-testid="back-to-insights"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-display text-white/80 hover:text-unik-gold mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> {t("insights.backToInsights").replace(/^←\s*/, "")}
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-white/85">
            <span className="eyebrow text-unik-gold">{category}</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="eyebrow text-white/70">{read}</span>
          </div>
          <h1
            className="mt-7 font-display tracking-tight max-w-4xl"
            style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)", fontWeight: 500, lineHeight: 1.05 }}
          >
            {title}
          </h1>
        </div>
      </section>

      {/* BODY */}
      <article className="relative bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-[760px] px-6 lg:px-10">
          {/* Author + share row */}
          <div data-reveal className="flex items-center justify-between pb-10 border-b border-[#14355a]/15">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">By</div>
              <div className="mt-1 font-display text-[#14355a]" style={{ fontWeight: 500 }}>
                {author}
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#14355a]/55 font-medium">
              {read}
            </div>
          </div>

          {/* Excerpt / lede */}
          <p
            data-reveal
            style={{ "--reveal-delay": "120ms" }}
            className="mt-12 text-[20px] lg:text-[22px] leading-[1.6] text-[#14355a] font-display tracking-tight"
          >
            {excerpt}
          </p>

          {/* Article paragraphs */}
          <div data-reveal style={{ "--reveal-delay": "220ms" }} className="mt-10 space-y-7">
            {body.map((item, i) => {
              if (typeof item === "string") {
                return (
                  <p key={`p-${i}-${item.slice(0, 24)}`} className="text-[17px] lg:text-[18px] leading-[1.85] text-unik-ink/85">
                    {item}
                  </p>
                );
              }
              if (item.type === "heading") {
                return (
                  <h2
                    key={`h-${i}-${item.text.slice(0, 24)}`}
                    className="mt-6 pt-4 font-display text-[#14355a] tracking-tight"
                    style={{ fontSize: "clamp(1.5rem, 2.2vw, 2rem)", fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {item.text}
                  </h2>
                );
              }
              if (item.type === "link") {
                return (
                  <a
                    key={`link-${i}-${item.href}`}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[16px] lg:text-[17px] text-unik-gold hover:text-[#14355a] font-display transition-colors underline underline-offset-4"
                  >
                    {item.label} <ArrowRight size={16} />
                  </a>
                );
              }
              if (item.type === "image") {
                return (
                  <figure key={`img-${i}-${item.src}`} className="my-10 lg:my-14">
                    <div className="overflow-hidden bg-[#14355a]/5">
                      <img
                        src={item.src}
                        alt={item.caption || title}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                    {item.caption ? (
                      <figcaption className="mt-4 text-[13px] lg:text-[14px] italic text-unik-ink/65 leading-[1.7] font-display">
                        {item.caption}
                      </figcaption>
                    ) : null}
                  </figure>
                );
              }
              if (item.type === "list") {
                return (
                  <ul key={`list-${i}-${(item.items[0] || "").slice(0, 24)}`} className="space-y-3 pl-5 list-disc marker:text-unik-gold">
                    {item.items.map((li) => (
                      <li key={li} className="text-[16px] lg:text-[17px] leading-[1.8] text-unik-ink/85">
                        {li}
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          {/* Footer rule + share placeholder */}
          <div className="mt-16 pt-10 border-t border-[#14355a]/15 flex items-center justify-between">
            <Link
              to="/insights"
              className="link-rule text-[#14355a] text-sm hover:text-unik-gold inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} /> {t("insights.allInsights")}
            </Link>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#14355a]/55">
              {t("insights.shareArticle")}
            </div>
          </div>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="relative bg-[#f3f4f6] py-24 lg:py-32">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div data-reveal className="mb-12">
              <div className="eyebrow text-[#14355a]">{t("insights.relatedReading")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
            </div>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
              {related.map((a, i) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  data-reveal
                  style={{ "--reveal-delay": `${i * 110}ms` }}
                  data-testid={`related-article-${a.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#14355a]/5">
                    <img
                      src={a.img}
                      alt={pickLocale(a, "title") || a.title}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex items-center gap-4">
                    <span className="eyebrow text-black">{pickLocale(a, "category") || a.category}</span>
                    <span className="w-1 h-1 bg-[#14355a]/30 rounded-full" />
                    <span className="eyebrow text-unik-ink/50">{pickLocale(a, "read") || a.read}</span>
                  </div>
                  <h3
                    style={{ fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)", fontWeight: 500 }}
                    className="mt-3 font-display text-[#14355a] tracking-tight leading-tight"
                  >
                    {pickLocale(a, "title") || a.title}
                  </h3>
                  <span className="inline-flex items-center gap-2 mt-4 text-[#14355a] text-xs uppercase tracking-[0.2em] font-display font-medium group-hover:text-unik-gold transition-colors">
                    {t("insights.readShort")} <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BottomCTA
        eyebrow={t("insights.bottomCta.eyebrow")}
        title={t("insights.bottomCta.title")}
        accent={t("insights.bottomCta.accent")}
        body={t("insights.bottomCta.body")}
        primary={{ label: t("insights.bottomCta.primary"), to: "/contact" }}
        secondary={{ label: t("insights.bottomCta.secondary"), to: "/insights" }}
      />

      <SiteFooter />
    </div>
  );
}

// (no extra exports needed)
